import { Task } from '@lit/task';
import { LitElement, css, html } from 'lit';
import { customElement, queryAll, state } from 'lit/decorators.js';
import { api } from './api.js';

import { clientName } from '@flakiness/server/common/knownClientIds.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { consume, contexts } from './contexts.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { consumeDOMEvent } from './utils.js';
import { tasks } from './tasks.js';

const pageSchema = {
  code: URLState.option<string>({
    name: 'code',
    default: '',
    encode: code => code ? code.slice(0, 4) + '-' + code.slice(4) : '',
    decode: code => code ? code.replaceAll('-', '') : code,
  }),
};

@customElement('page-device-authentication')
export class PageDeviceAuthentication extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [ '/device', '/device/' ],
      render: (groups) => html`<page-device-authentication></page-device-authentication>`,
    }];
  }

  static url(): string {
    return platformUrl('/device');
  }

  @consume({ context: contexts.user, subscribe: true })
  private _user?: ContextType<typeof contexts.user>;

  @consume({ context: contexts.serverInfo, subscribe: true })
  private _serverInfo?: ContextType<typeof contexts.serverInfo>;
  private _urlState = new URLState(this, pageSchema);

  @state() private _status?: 'approved'|'rejected';

  private _requestTask = new Task(this, {
    args: () => [this._urlState.state.code, this._user] as const,
    task: async ([userCode, user], { signal }) => {
      CHECK(userCode && user && userCode.length === 8);
      return await api.deviceauth.getRequest.GET({ userCode });
    }
  });

  @queryAll('input.char') private _charInputs?: HTMLInputElement[];

  private _onInput(e: InputEvent) {
    if (!e.target || !this._charInputs || !(e.target instanceof HTMLInputElement))
      return;
    this._fillInput(e.target, e.data ?? undefined);
  }

  private _fillInput(input: HTMLInputElement, value?: string) {
    if (!this._charInputs)
      return;
    const allInputs = Array.from(this._charInputs);
    const inputIndex = allInputs.indexOf(input);
    if (!value) {
      input.value = '';
      const prevInput = allInputs[inputIndex - 1];
      this._urlState.update({ code: allInputs.map(i => i.value).join('') });

      prevInput?.focus();
      prevInput?.select();
    } else {
      for (let i = 0; i < value.length && i + inputIndex < allInputs.length; ++i)
        allInputs[i + inputIndex].value = value[i].toUpperCase();
      this._urlState.update({ code: allInputs.map(i => i.value).join('') });

      const nextInput = allInputs.at(inputIndex + value.length) ?? allInputs.at(-1);
      nextInput?.focus();
      nextInput?.select();
    }
  }

  private _onFocus(e: FocusEvent) {
    if (!e.target || !(e.target instanceof HTMLInputElement) || !this._charInputs)
      return;
    const t: HTMLInputElement = e.target;
    const allInputs = Array.from(this._charInputs);
    let index = allInputs.indexOf(t);
    const toFocus = Math.min(this._urlState.state.code.length, index);
    if (toFocus === index)
      t.select();
    else
      allInputs[toFocus]?.focus();
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (!e.target || !(e.target instanceof HTMLInputElement) || !this._charInputs)
      return;
    const allInputs = Array.from(this._charInputs);
    const inputIndex = allInputs.indexOf(e.target);
    if (e.key === 'ArrowLeft') {
      allInputs[inputIndex - 1]?.focus();
      consumeDOMEvent(e);
    } else if (e.key === 'ArrowRight') {
      allInputs[inputIndex + 1]?.focus();
      consumeDOMEvent(e);
    } else if (e.key === 'Backspace') {
      this._fillInput(e.target, undefined);
      consumeDOMEvent(e);
    } else {
      e.target.select();
    }
  }

  private _onPaste(e: ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData?.getData('text').trim().replaceAll('-', '');
    if (!e.target || !(e.target instanceof HTMLInputElement) || !pasted)
      return;
    this._fillInput(e.target, pasted);
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Device Authentication' }),
  });

  render() {
    const request = this._requestTask.value;
    return html`
      <app-header .customTitle=${'Device Authentication'}></app-header>
      <app-body-card>
        ${!request ? this._renderUserCode() :
          this._renderRequest(request)
        }
      </app-body-card>
      <app-footer></app-footer>
    `;
  }

  private async _onApprove(deviceCode: string) {
    await api.deviceauth.approveRequest.POST({ deviceCode });
    this._status = 'approved';
  }

  private async _onReject(deviceCode: string) {
    await api.deviceauth.rejectRequest.POST({ deviceCode });
    this._status = 'rejected';
  }

  private _renderUserCode() {
    const code = this._urlState.state.code;
    return html`
      <h1>User Code</h1>
      <section class=code
        @paste=${this._onPaste}
        @keydown=${this._onKeyDown} 
        @input=${this._onInput}
      >
        ${Array(8).fill(0).map((_, idx) => html`
          <input @focus=${this._onFocus} ?autofocus=${idx === code.length} class=char maxlength=1 value=${code[idx]}>
        `)}
        <span>-</span>
      </section>
      ${code.length === 8 ? html`
        <div>No authentication request found for this code. Please verify the code and try again.</div>
      ` : html`<div>Enter the code you received on your device.</div>`}
    `;
  }

  private _renderRequest(request: WireTypes.DeviceAuthRequest) {
    if (this._status === 'approved') {
      return html`
        <div class=request-approved>
          <h1>Device Approved</h1>
          <p>Device "${request.deviceName}" has been successfully approved!</p>
          <p>You can now close this page.</p>
        </div>
      `;
    }
    if (this._status === 'rejected') {
      return html`
        <div class=request-rejected>
          <h1>Authorization cancelled</h1>
          <p>Device "${request.deviceName}" authorization has been cancelled.</p>
          <p>You can now safely close this window and return to your terminal.</p>
        </div>
      `;
    }

    const code = this._urlState.state.code;

    return html`
      <div>
        <h1>Authenticate ${clientName(request.clientId)}?</h1>
        <div style="max-width: 80ch">
          <p>A device named <strong>"${request.deviceName}"</strong> is requesting permission to act on your behalf.</p>
          <p><strong>Confirmation Code:</strong> <code style="background: yellow">${code.substring(0, 4) + '-' + code.substring(4)}</code>
          <p>
          <i>Check that this code matches the one shown in your terminal.</i>
          </p>
        </div>
        <sl-divider></sl-divider>
        <h-box>
          <sl-button @click=${() => this._onReject(request.deviceCode)}>Reject</sl-button>
          <sl-button variant=primary @click=${() => this._onApprove(request.deviceCode)}>Authorize</sl-button>
        </h-box>
      </div>
    `;
  }

  static styles = [linkStyles, pageStyles, css`
    :host {
      background: var(--fk-color-canvas);
    }

    app-body-card:has(.request-rejected)::part(base) {
      background-color: var(--sl-color-danger-100);
    }

    app-body-card:has(.request-approved)::part(base) {
      background-color: var(--sl-color-success-100);
    }

    .code {
      display: grid;
      grid-template-columns: repeat(9, auto);
      align-items: center;
      justify-content: center;
      gap: var(--sl-spacing-small);

      --font-size: var(--sl-font-size-4x-large);

      span {
        font-size: var(--font-size);
        grid-row: 1;
        grid-column: 5;
      }

      input {
        font-size: var(--font-size);
        width: 1em;
        height: 1em;
        text-align: center;
        caret-color: transparent;

        &::selection {
          background: transparent;
        }
      }

      input:focus {
        background: yellow;
      }
    }

    .sessions {
      display: grid;
      grid-template-columns: auto 1fr auto;
      border: 1px solid var(--fk-color-border);
      border-radius: var(--sl-border-radius-large);
    }

    .session {
      display: grid;
      grid-column: 1/-1;
      grid-template-columns: subgrid;
      padding: var(--sl-spacing-small);
      gap: var(--sl-spacing-small);
      align-items: center;

      &.header {
        font-weight: bold;
        background: var(--sl-color-neutral-50);
      }

      &:not(.header):hover {
        background: var(--fk-color-tablerow-hover);
      }
    }
  `];
}
